/*
 * ---------------------------------------------------------------------------
 * COMMERCIAL IN CONFIDENCE
 *
 * (c) Copyright Quosient Ltd. All Rights Reserved.
 *
 * See LICENSE.txt in the repository root.
 * ---------------------------------------------------------------------------
 */
import { countries } from '@/countryData/countryList.js';
import { strataZones, strataCountries } from '@/countryData/countryList.js';
import { Subject } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import ShapeMapType  from '@/components/ResultMap/CustomMapTypes/ShapeMapType.js';

/**
 * service contains areas and shapes,
 * areas are collections of shapes, such that
 * areas: [{
 *  id: String,
 *  colour: String
 * }]
 * sometimes areas may not have an id field and instead have a name field,
 * these are the same
 *
 * shapes can be of two types (currently), "UserDrawn" and "UserUploaded"
 *
 * We may also consider adding country bounds so that when a user selects a country
 * we pan and outline it in the map (look at EarthMap for example)
 *
 * shapes: [{
 *   id: String, (uuid), required
 *   areaID: String, required - foreign key
 *   type: String (UserDrawn or UserUploaded), required,
 *   overlay: GoogleMapsOverlay, required for UserDrawn and will be created for UserUploaded,
 *   bbox: {
 *     'SW': [Number, Number],
 *     'NE': [Number, Number]
 *   }, required for UserUploaded
 *   mapURL: String, required for UserUploaded,
 *   assetID: String, required for UserUploaded
 *  }]
 */

let service = {
    areas: [],
    shapes: [],
    selectedArea: {},

    // RXJS Subjects
    shapeUpdated: new Subject(),
    newCoords: new Subject(),
    featuredBlockEvent: new Subject(),
    clearAreas: new Subject(),
    updatedDatasetBbox: new Subject(),
    areaChange: new Subject(),
    userUploadedShape: new Subject()
}

const AreaService = {
    // GETTERS
    /**
     * Returns all custom areas AND countries in blockly dropdown format
     *
     * @param {String} blockCategory optional - if not provided, returns all areas else returns areas for the given block category ("strata")
     * @returns
     */
    getAreas(blockCategory) {
        if(blockCategory === "strata") {
            return {'zones': strataZones.slice(), 'countries': Object.assign({}, strataCountries)};
        } else {
            return this.getUserAreas().concat(countries.slice());
        }
    },
    /**
     * Returns user defined areas
     *
     * @returns {Array} - array of user areas in blockly dropdown format
     */
    getUserAreas() {
        if(service.areas.length === 0) {
            // if there are no custom areas return NA
            return [["No area selected", "NA"]];
        } else {
            return service.areas.map(area => [area.id, area.id]);
        }
    },
    /**
     * For a given area name get all shapes associated with it
     * @param {String} areaName
     * @returns {Array} - array of shapes
     */
    // supercedes getPolygonsForArea method
    getShapesForArea(areaName) {
        let shapes = [];
        service.shapes.forEach(shape => {
            if(shape.areaID === areaName) {
                shapes.push(shape);
            }
        });
        return shapes;
    },
    /**
     * Returns all user uploaded assets
    */
    // supercedes getUserUploadedAsset - the behaviour of which will need
    // to be updated to support multiple assets
    getUserUploadedAssets() {
        let assets = [];
        service.shapes.forEach(shape => {
            if(shape.type === "UserUploaded") {
                assets.push(shape);
            }
        });
        return assets;
    },
    /**
     * Returns all custom area names
     */
    getCustomAreas() {
        return service.areas.map(area => area.id);
    },
    /**
     * Returns the coordinates for a given UserDrawnShape,
     * supports polygons and rectangles
     * @param {Object} shapeObject
     * @returns {Array} coordinates - 2D array of coordinates
     */
    getCoordinatesForUserDrawnShape(shapeObject){
        if(shapeObject.type !== "UserDrawn") {
            throw new Error("Incorrect Shape Type: Attempted to get coordinates for a non-UserDrawn shape")
        }

        let overlay = shapeObject.overlay;

        if(overlay.getBounds) {
            // if overlay has getBounds method, it is a rectangle
            let bounds = overlay.getBounds();
            var NE = bounds.getNorthEast();
            var SW = bounds.getSouthWest();

            return [
                [NE.lng(), NE.lat()],
                [SW.lng(), NE.lat()],
                [SW.lng(),SW.lat()],
                [NE.lng(),SW.lat()]
            ];
        } else if (overlay.getPath) {
            // if overlay has getPath method, it is a polygon
            let polygonPath = overlay.getPath();
            let coordinates = [];
            for (let i = 0; i < polygonPath.length; i++) {
                coordinates.push([
                    polygonPath.getAt(i).lng(),
                    polygonPath.getAt(i).lat()
                ]);
            }
            return coordinates;
        }
    },
    /**
     * Returns areas and shapes object in format for generating run doc.
     * Shapes object will contain a coordinates property which is a 2D array of coordinates if the shape is a UserDrawn shape
     * Optional param areaIds - if provided, only returns shapes for those areas
     * Shapes in the format: {
     *  id: String,
     *  areaID: String,
     *  type: String (UserDrawn or UserUploaded),
     *  coordinates: [[Number, Number], [Number, Number], ...]]
     * or if UserUploaded the normal fields
     * }
     * @param {Array} areaIds - optional field for filtering the areas to get
     * @returns {Object} - object containing areas and shapes
     */
    getAreasAndShapesForRunDoc(areaIds) {
        let areasAndShapes = {
            areas: [],
            shapes: []
        };

        // creates an array of valid IDs
        let validAreaIds = [];
        if(areaIds) {
            validAreaIds = areaIds;
        } else {
            validAreaIds = this.getCustomAreas();
        }

        // gets valid areas
        service.areas.forEach(area => {
            if(validAreaIds.includes(area.id)) {
                areasAndShapes.areas.push(area);

                // gets valid shapes and if shape is UserDrawn, adds coordinates
                let validShapes = this.getShapesForArea(area.id)
                validShapes.forEach(shape => {
                    // make a copy of shape, so that we don't modify the original
                    let shapeCopy = Object.assign({}, shape);
                    if(shapeCopy.type === "UserDrawn") {
                        shapeCopy.coordinates = this.getCoordinatesForUserDrawnShape(shape);
                        // remove overlay key as it's circular, which cause issues in JSON.stringify
                        delete shapeCopy.overlay;
                    } else if (shapeCopy.type === "UserUploaded") {
                        // remove unnecessary fields, bbox and mapURL
                        delete shapeCopy.bbox;
                        delete shapeCopy.mapURL;
                        // remove overlay key as it's circular, which cause issues in JSON.stringify
                        delete shapeCopy.overlay;
                    }
                    areasAndShapes.shapes.push(shapeCopy);
                });

            }
        });

        return areasAndShapes;
    },
    /** returns the selected area
     * @returns {Object} - area object
     */
    getSelectedArea() {
        return service.selectedArea;
    },


    // ADDERS
    /**
     * For a given areaName and colour add the area to the service.areas array
     * @param {String} areaName
     * @param {String} colour
     */
    // supersedes the addCustomArea method
    addArea(areaName, colour) {
        service.areas.push({
            id: areaName,
            colour: colour
        });

        service.areaChange.next();
    },
    /**
     * For a given UserDrawn overlay, add the shape to the service.shapes array
     * @param {String} areaID - foreign key to the area
     * @param {GoogleMapsOverlay} overlay - the overlay to be added
     * @returns {GoogleMapsOverlay} - the overlay that was added with areaId and shapeId properties
     */
    // supercedes the addPolygon method
    addUserDrawnShape(areaID, overlay) {
        let shape = {
            id: this.generateUUID(),
            areaID: areaID,
            type: "UserDrawn",
            overlay: overlay
        }

        service.shapes.push(shape);

        this.publishShapeUpdate(shape);

        overlay.areaId = areaID;
        overlay.shapeId = shape.id;

        return overlay;
    },
    /**
     * For a given UserUploaded shape, add the shape to the service.shapes array
     * If areaID is null, it will create a new area and assign the shape to it
     * @param {*} areaID - foreign key to the area
     * @param {*} bbox - bounding box of the shape
     * @param {*} mapURL - url of the map
     * @param {*} assetID - asset id of the shape
     */
    // supercedes the polygonUploaded method
    addUserUploadedShape(areaID, bbox, mapURL, assetID) {
        // informs area service that it needs to create an area
        // then add the shape to that area
        // I'm not a fan of this pattern, I think we should be using state

        let shape = {
            id: this.generateUUID(),
            areaID: areaID,
            type: "UserUploaded",
            bbox: bbox,
            mapURL: mapURL,
            assetID: assetID
        }


        service.shapes.push(shape);

        this.publishShapeUpdate(shape);
        // inform asset service a new asset has been added
        // also informs google map areas view so we can add it to the map
        service.userUploadedShape.next(shape);
    },

    // REMOVERS
    /**
     * for a given area object such that {
     *  id: String,
     *  colour: String
     * }
     * It will remove the area the service.areas array
     * @param {Object} area
     */
    removeArea(area) {
        let index = this.findAreaIndex(area.id);
        if (index === -1) {
            return;
        }
        service.areas.splice(index, 1);


        // remove shapes associated with this area
        this.removeAreaShapes(area.id);

        service.areaChange.next();
    },
    /**
     * For a given shapeObject, remove it from the service.shapes array
     * @param {Object} shapeObject
     */
    removeShape(shapeObject) {
        let index = service.shapes.findIndex(shape => shape.id === shapeObject.id);
        if (index === -1) {
            return;
        }
        let shape = service.shapes[index];
        this.removeShapeFromMap(shape);
        service.shapes.splice(index, 1);

        this.publishShapeUpdate(shape)
    },
    /**
     * For a given areaName, remove all shapes associated with that area
     * @param {String} areaName
     */
    // supercedes the removeAreaPolygons method
    removeAreaShapes(areaName) {
        // get shapes associated with this area
        let shapes = this.getShapesForArea(areaName);

        // remove shapes
        shapes.forEach(shape => {
            this.removeShape(shape);
        });
    },
    /**
     * Removes all areas
     */
    removeAllAreas() {
        service.areas = [];
        service.clearAreas.next();
    },
    /**
     * Removes all shapes
     */
    removeAllShapes() {
        // array can't be emptied only, we must remove one by one from the map
        // must create a copy of the array, as we are removing from the array as we go
        let shapesClone = [...service.shapes]
        shapesClone.forEach(shape => {
            this.removeShape(shape);
        })
    },
    /**
     * For a given shapeObject, remove it from the map
     * @param {Object} shapeObject
     */
    removeShapeFromMap(shapeObject) {
        if(shapeObject.type === "UserDrawn") {
            shapeObject.overlay.setMap(null);
        } else if(shapeObject.type === "UserUploaded") {
            shapeObject.overlay.remove();
            shapeObject.overlay = null;
        }
    },
    /**
     * For a given shapeID, remove it from the map and service
     * @param {String} shapeID
     */
    removeShapeByID(shapeID) {
        let index = this.findShapeIndex(shapeID);
        if (index === -1) {
            return;
        }
        let shape = service.shapes[index];

        this.removeShape(shape);
    },

    // UPDATERS
    /**
     * For a given area object such that {
     * id: String,
     * colour: String
     * }
     * It will update the area in the service.areas array
     * @param {Object} area
     */
    // Supersedes the changedColour method
    updateArea(area) {
        let index = this.findAreaIndex(area.id);
        if (index === -1) {
            return;
        }
        service.areas[index] = area;
        this.updateShapesByArea(area.id, area.colour);
    },
    /**
     * For a given shapeObject, update it in the service.shapes array
     * @param {Object} shapeObject
     */
    updateShape(shapeObject) {
        let index = this.findShapeIndex(shapeObject.id);
        if (index === -1) {
            return;
        }
        service.shapes[index] = shapeObject;
        this.publishShapeUpdate(shapeObject);
    },
    /**
     * For a given shapeID update it's areaID
     * @param {String} shapeID
     * @param {String} areaID
     * @param {GoogleMapsOverlay} overlay
     * @returns {GoogleMapsOverlay} - the overlay that was updated with areaId and shapeId properties
     */
    updateAreaIdOfShape(shapeID, areaID, overlay) {
        let index = this.findShapeIndex(shapeID);
        if (index === -1) {
            return;
        }
        service.shapes[index].areaID = areaID;

        overlay.areaId = areaID;
        service.shapes[index].overlay = overlay;
        // emit shape update event
        this.publishShapeUpdate(service.shapes[index]);
        return overlay
    },
    /**
     * For a given areaID and colour, update the
     * areaID and colours of all shapes associated with that area
     * @param {String} areaID
     * @param {String} colour
     */
    updateShapesByArea(areaID, colour) {
        // get shapes associated with this area
        let shapes = this.getShapesForArea(areaID);
        console.log("updating shapes for area", areaID, shapes)
        shapes.forEach(shape => {
            shape.areaID = areaID;
            console.log("updating shape", shape)
            if(shape.type === "UserDrawn") {
                shape.overlay.setOptions({
                    strokeColor: colour
                });
            } else if (shape.type === "UserUploaded") {
                shape.overlay.setColour(colour);
            }
            this.publishShapeUpdate(shape);
        })
    },


    // SETTERS
    /**
     * For a given shapeObject and map, set the shape on the map using custom map type
     * @param {Object} shapeObject
     * @param {GoogleMapsMap} map
     * @param {GoogleMaps} google - the google object
     * @param {String} colour
     */
    setShapeOnMap(shapeObject, map, google, colour) {
        if(shapeObject.type === "UserDrawn") {
            return;
        } else if(shapeObject.type === "UserUploaded") {
            // create new shape map overlay
            let shapeMap = new ShapeMapType(shapeObject.mapURL, shapeObject.shapeID, map, google, colour);
            shapeObject.overlay = shapeMap.overlay;
        }
    },

    /** sets selected area
     * @param {Object} areaId
     * @param {String} colour
     */
    setSelectedArea(areaId, colour) {
        service.selectedArea = {
            id: areaId,
            colour: colour
        };
    },

    // UTILITY FUNCTIONS
    /**
     * For a given area name, find the index of the area, returns -1 if area doesn't exist
     * @param {String} areaName
     * @returns {Number} - index of area in service.areas or -1 if area doesn't exist
     */
    findAreaIndex(areaName) {
        return service.areas.findIndex(area => area.id === areaName);
    },
    /**
     * For a given shapeID, find the index of the shape, returns -1 if shape doesn't exist
     * @param {String} shapeID
     */
    findShapeIndex(shapeID) {
        return service.shapes.findIndex(shape => shape.id === shapeID);
    },
    /**
     * For a given area name, return the hex colour of the area, returns null if not found
     * @param {String} areaName
     * @returns {String} areaColour - null if area not found
     */
    getColour(areaName) {
        let index = this.findAreaIndex(areaName);
        if (index === -1) {
            return null;
        }
        return service.areas[index].colour;
    },
    /** Generates a UUID
     * @returns {String} - UUID
     */
    generateUUID() {
        return uuidv4();
    },
    /**
     * Clears the map of all shapes and areas, emits clearAreas event
     */
    clearMap() {
        this.removeAllAreas();
        this.removeAllShapes();
        service.clearAreas.next();
        service.areaChange.next();
    },
    /**
     * Loops over assets to see if there are any userUploadedAssets, will return true if there is at least one. Use getUserUploadedAssets to get the assets/
     */
    hasUserUploadedAssets() {
        return service.shapes.some(shape => shape.type === "UserUploaded");
    },
    /**
     * Checks if areaName is a custom area
     * @param {String} areaName
     * @returns {Boolean}
     */
    isCustomArea(areaName) {
        return this.findAreaIndex(areaName) !== -1;
    },

    // EVENTS AND PUBLISHERS
    /**
     * Removes all areas, and all shapes, then publishes a new polygon via the newCoords observable
     * @param {Object} params - params object {
     *  area: String,
     *  xml: String,
     *  mapType: String,
     *  isAreaOnly: Boolean
     * }
     */
    publishPolygon(params){
        this.removeAllAreas();
        this.removeAllShapes();
        service.newCoords.next(params);
    },
    /**
     * Publishes a new featuredBlockEvent using the params given
     *
     * publish a new event to the featuredBlockEvent observable. We need to do this here because we can't use .next() from within the vue components
     *
     * @param {Object} params - params object {
     * area: String,
     * xml: String,
     * mapType: String,
     * isAreaOnly: Boolean
     * }
     */
    publishFeatureEvent(params) {
        service.featuredBlockEvent.next(params);
    },
    /**
     * Publish the updatedDatasetBbox event
     * @param {Object} bbox - bounding box of dataset
     * @param {String} blockId - ID of block
     */
    // supercedes updateDatasetBbox method
    publishUpdatedDatasetBbox(bbox, blockId) {
        service.updatedDatasetBbox.next({
            bbox: bbox,
            blockId: blockId
        });
    },
    /**
     * For a given shape emit shapeUpdated event
     * Kept for legacy purposes, consider updating
     *
     * Supercedes the updatePolygon method
     */
    publishShapeUpdate(shape) {
        service.shapeUpdated.next({
            areaId: shape.areaID,
            shapeId: shape.id
        });
    },

    // RXJS OBSERVABLES
    /**
     * shapeUpdated observable, used to inform blocks that one of the shapes has been updated
     * and that an area may have been updated too
     * emitted with the areaName the shape was associated too as well as the shapeId
     * Supercedes the polygonUpdated event
     */
    shapeUpdated$: service.shapeUpdated.asObservable(),
    /**
     * newCoords observable, used when a feature workflow  is created to clear the map
     * and emit the featured workflow polygons and add them to the map
     */
    newCoords$: service.newCoords.asObservable(),
    /**
     * featuredBlockEvent observable, used to publish new featured block to workspace
     * This must be done at separate times to the newCoords observable, to avoid issues with asynchronous timings
     */
    featuredBlockEvent$: service.featuredBlockEvent.asObservable(),
    /**
     * clearAreas observable, used to publish clear areas event which nukes the map completely from all polygons, areas, shapes and layers
     */
    clearAreas$: service.clearAreas.asObservable(),
    /**
     * updatedDatasetBbox observable, used to publish updated dataset bbox event which creates a bbox overlay on the map for data selector blocks - we may want to update this to be more general and to allow for multiple overlays, this could be helpful for country bounds
     */
    updatedDatasetBbox$: service.updatedDatasetBbox.asObservable(),
    /**
     * areaChange observable, used to publish when a new area has been created to inform blocks they need to update their area dropdown
    */
    areaChange$: service.areaChange.asObservable(),
    /**
     * userUploadedShape observable, used to publish user uploaded polygon event, currently
     * used in basemap view to render userUploadedShape but this is going to be phased out.
     * Most likely will be used to render userUploadedShape in the map
     * supercedes userUploadedPolygon event
     */
    userUploadedShape$: service.userUploadedShape.asObservable(),
}

export {
    AreaService
}